// The few builders the hub cards need in the look of the ☸ OPTIONS menu
// and the kit of `ui/options/kit.ts` lacks (ui-map-interface.md §16.14,
// R1731).
//
// The ALERTS and BLOCKED cards stand in the hub SETTINGS panel and in the
// game menu, so their markup must read the same under `#hub` and under
// `#ui`. `ui/options/cards.css` holds the look of every class here, under
// the kit root `.opt`, and no rule of `hub.css` reaches one of them. Like
// the kit, a builder escapes a text parameter and takes a parameter named
// `body` as markup the caller built.

import type { SfxId } from '../../../audio/triggers';
import { messageOf } from '../../../net/api';
import { esc } from '../../esc';
import { pressAttrs } from '../../choice/choice';
import type { PanelContext } from '../panel';
import { UNCHARTED_MARK } from '../uncharted';

/** The refusal line under a control, or '' while nothing was refused. A
 *  server's own message reaches it as text and never as markup. */
export function errNote(text: string): string {
  return text === '' ? '' : `<div class="c-err">${esc(text)}</div>`;
}

/** The line a call that went through leaves behind. */
export function okNote(text: string): string {
  return `<div class="c-ok">${esc(text)}</div>`;
}

/** The honest empty state of a list: what is not there, and why. It wears
 *  the mark of uncharted water that `panel.ts` `empty` wears (R1742). The
 *  mark takes the Faint token here, because `ui/options/cards.css` holds
 *  no rule for it and the card stands in the game menu as well. */
export function emptyNote(what: string, why = ''): string {
  return `<div class="c-empty"><span style="color:var(--faint)">${UNCHARTED_MARK}</span>`
    + `<b>${esc(what)}</b>${why === '' ? '' : `<i>${esc(why)}</i>`}</div>`;
}

/** A quiet word in the place of a control: CHECKING while a read travels,
 *  LINKED for a method the account holds. `on` paints it in the accent. */
export function stateWord(text: string, on = false): string {
  return `<span class="c-state${on ? ' on' : ''}">${esc(text)}</span>`;
}

/** One call of a row that writes at once: whether it is in flight, and
 *  the last refusal as a person reads it ('' for none). */
export interface HeldCall {
  busy: boolean;
  err: string;
}

/**
 * Runs one call of a row that writes at once, never through the blob: the
 * standing orders (R1256), the advisor (R1566), the stated hours (R1304)
 * and LIFT of a block. The press plays `cue` and the row redraws held; a
 * refusal plays the error cue and stands under the row; the row redraws
 * free when the call ends. `ok` takes the answer. The caller checks
 * `busy` before it calls.
 */
export function runHeld<T>(
  ctx: Pick<PanelContext, 'play' | 'refresh'>, st: HeldCall, cue: SfxId,
  call: () => Promise<T>, ok: (out: T) => void,
): void {
  st.busy = true;
  st.err = '';
  ctx.play(cue);
  ctx.refresh();
  void call().then(ok, (e: unknown) => { st.err = messageOf(e); ctx.play('sfx-menu-error'); })
    .finally(() => { st.busy = false; ctx.refresh(); });
}

/**
 * One fold of a page (R1563): a button that carries the heading and
 * `aria-expanded`, and the body while the fold stands open. `aria-controls`
 * names the body only while the body is in the markup. `bad` paints the
 * heading red, for the account delete.
 */
export function fold(o: {
  act: string; key: string; title: string; open: boolean; body: string; bad?: boolean;
}): string {
  const id = `${o.act}-${o.key}`;
  return `<div class="c-fold${o.open ? ' open' : ''}">`
    + `<button type="button" class="c-foldb${o.bad ? ' bad' : ''}"${pressAttrs({ act: o.act, key: o.key })}`
    + ` aria-expanded="${o.open}"${o.open ? ` aria-controls="${id}"` : ''}><b>${esc(o.title)}</b></button>`
    + (o.open ? `<div class="c-foldbody" id="${id}">${o.body}</div>` : '')
    + '</div>';
}
